import React, { useState, useRef, useEffect } from 'react';
import { Search, X, Plus, Check, ChevronUp, ChevronDown } from 'lucide-react';

export function CombinedSkillsInput({
  options = [],
  value = { skills: [] },
  onChange,
  placeholder = 'Search skills...',
  className = '',
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedSkills = value.skills || [];

  // Build display list from selected {skill_id, skill_name} objects
  const allSelectedSkills = React.useMemo(
    () =>
      selectedSkills.map((s) => ({
        value: s.skill_id ?? s.skill_name,
        label: s.skill_name,
      })),
    [selectedSkills]
  );

  // Group options by category if available
  const groupedOptions = React.useMemo(() => {
    const hasCategories = options.some(
      (opt) => opt.category || opt.category_name
    );
    if (!hasCategories) {
      return options.length > 0 ? { 'All Skills': options } : {};
    }

    const grouped = {};
    options.forEach((opt) => {
      const category = opt.category || opt.category_name || 'Other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(opt);
    });
    const sorted = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
    const sortedGrouped = {};
    sorted.forEach((key) => {
      sortedGrouped[key] = grouped[key];
    });
    return sortedGrouped;
  }, [options]);

  // Filter options based on search
  const filteredGroupedOptions = React.useMemo(() => {
    if (!searchTerm.trim()) return groupedOptions;

    const filtered = {};
    Object.keys(groupedOptions).forEach((category) => {
      const filteredItems = groupedOptions[category].filter(
        (opt) =>
          opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (opt.abbreviation &&
            opt.abbreviation.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      if (filteredItems.length > 0) {
        filtered[category] = filteredItems;
      }
    });
    return filtered;
  }, [searchTerm, groupedOptions]);

  // Expand all categories by default
  useEffect(() => {
    const categories = Object.keys(groupedOptions);
    const initialExpanded = {};
    categories.forEach((cat) => {
      initialExpanded[cat] = true;
    });
    setExpandedCategories(initialExpanded);
  }, [groupedOptions]);

  // Check if search term matches any existing option
  const hasExactMatch = React.useMemo(() => {
    if (!searchTerm.trim()) return false;
    return options.some(
      (opt) =>
        opt.label.toLowerCase() === searchTerm.toLowerCase().trim() ||
        opt.value.toLowerCase() === searchTerm.toLowerCase().trim()
    );
  }, [searchTerm, options]);

  // Check if search term is already selected (by name)
  const isSearchTermSelected = React.useMemo(() => {
    if (!searchTerm.trim()) return false;
    const searchLower = searchTerm.toLowerCase().trim();
    return selectedSkills.some(
      (s) => s.skill_name.toLowerCase() === searchLower
    );
  }, [searchTerm, selectedSkills]);

  // Inject scrollbar styles
  useEffect(() => {
    const styleId = 'skills-scrollbar-styles';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .skills-scrollbar::-webkit-scrollbar {
        width: 6px;
      }
      .skills-scrollbar::-webkit-scrollbar-track {
        background: transparent;
      }
      .skills-scrollbar::-webkit-scrollbar-thumb {
        background: var(--ah-border-card);
        border-radius: 3px;
      }
      .skills-scrollbar::-webkit-scrollbar-thumb:hover {
        background: var(--ah-border-card-light);
      }
      .skills-scrollbar {
        scrollbar-width: thin;
        scrollbar-color: var(--ah-border-card) transparent;
      }
      .skills-chips-scrollbar::-webkit-scrollbar {
        width: 4px;
        height: 4px;
      }
      .skills-chips-scrollbar::-webkit-scrollbar-track {
        background: transparent;
      }
      .skills-chips-scrollbar::-webkit-scrollbar-thumb {
        background: var(--ah-border-card);
        border-radius: 2px;
      }
      .skills-chips-scrollbar {
        scrollbar-width: thin;
        scrollbar-color: var(--ah-border-card) transparent;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, []);

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  // Check if an option from masterData is already selected
  const isOptionSelected = (option) => {
    const refId = parseInt(option.value, 10);
    return selectedSkills.some(
      (s) => s.ref_skill_id === refId || s.skill_name === option.label
    );
  };

  const handleToggleSkill = (option) => {
    if (isOptionSelected(option)) {
      const refId = parseInt(option.value, 10);
      onChange({
        ...value,
        skills: selectedSkills.filter(
          (s) => s.ref_skill_id !== refId && s.skill_name !== option.label
        ),
      });
    } else {
      onChange({
        ...value,
        skills: [
          ...selectedSkills,
          {
            skill_id: null,
            ref_skill_id: parseInt(option.value, 10),
            skill_name: option.label,
          },
        ],
      });
    }
  };

  const handleAddCustom = () => {
    if (!searchTerm.trim() || isSearchTermSelected) return;

    onChange({
      ...value,
      skills: [
        ...selectedSkills,
        { skill_id: null, skill_name: searchTerm.trim() },
      ],
    });
    setSearchTerm('');
  };

  const handleRemoveSkill = (skill) => {
    onChange({
      ...value,
      skills: selectedSkills.filter((s) => s.skill_name !== skill.label),
    });
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleInputKeyDown = (e) => {
    if (
      e.key === 'Enter' &&
      searchTerm.trim() &&
      !hasExactMatch &&
      !isSearchTermSelected
    ) {
      e.preventDefault();
      handleAddCustom();
    }
  };

  return (
    <div className={`relative w-full ${className}`} ref={containerRef}>
      {/* Selected Skills Chips */}
      {allSelectedSkills.length > 0 && (
        <div className="max-h-[140px] overflow-y-auto overflow-x-hidden skills-chips-scrollbar mb-3">
          <div className="flex flex-wrap gap-2">
            {allSelectedSkills.map((skill) => (
              <span
                key={`${skill.value}-${skill.label}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-ah-brand-primary text-white text-xs font-medium flex-shrink-0"
              >
                <span>{skill.label}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveSkill(skill)}
                  className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                  aria-label={`Remove ${skill.label}`}
                >
                  <X size={12} className="text-white" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Search Input */}
      <div className="relative flex items-center w-full rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-2.5 text-sm mb-3 focus-within:ring-2 focus-within:ring-ah-brand-primary focus-within:border-ah-brand-primary">
        <Search className="h-4 w-4 text-[var(--text-faint)] mr-2 flex-shrink-0" />
        <input
          ref={searchInputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          placeholder={placeholder}
          className="flex-1 outline-none bg-transparent text-sm text-[var(--text)] placeholder:text-[var(--text-faint)]"
        />
        {searchTerm && (
          <button
            type="button"
            onClick={handleClearSearch}
            className="ml-2 text-[var(--text-faint)] hover:text-[var(--text-muted)] transition-colors flex-shrink-0"
            aria-label="Clear search"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Skills List - Always Visible */}
      <div className="border border-[var(--border)] rounded-md bg-[var(--surface)] overflow-hidden">
        <div
          className="overflow-y-auto skills-scrollbar"
          style={{ minHeight: '5vh', maxHeight: '35vh' }}
        >
          {/* Show "No match" message and "Add as custom" button if search has no results */}
          {searchTerm.trim() &&
            Object.keys(filteredGroupedOptions).length === 0 &&
            !hasExactMatch &&
            !isSearchTermSelected && (
              <div className="p-8 text-center">
                <Search
                  size={24}
                  className="mx-auto mb-2 opacity-50 text-[var(--text-faint)]"
                />
                <p className="text-sm text-[var(--text-faint)] !text-center mb-4">
                  No match for{' '}
                  <span className="font-semibold">
                    &quot;{searchTerm}&quot;
                  </span>
                </p>
                <button
                  type="button"
                  onClick={handleAddCustom}
                  className="mx-auto flex items-center gap-2 px-4 py-2.5 rounded-md bg-ah-brand-primary text-white font-medium text-sm transition-colors hover:bg-ah-brand-alt-hover"
                >
                  <Plus size={16} />
                  Add{' '}
                  <span className="font-semibold">
                    &quot;{searchTerm}&quot;
                  </span>{' '}
                  as custom
                </button>
              </div>
            )}

          {/* Grouped Options */}
          {Object.keys(filteredGroupedOptions).length > 0 && (
            <div>
              {Object.entries(filteredGroupedOptions).map(
                ([category, categoryOptions]) => {
                  const isExpanded = expandedCategories[category] !== false;
                  return (
                    <div
                      key={category}
                      className="border-b border-[var(--border-light)] last:border-b-0"
                    >
                      <button
                        type="button"
                        onClick={() => toggleCategory(category)}
                        className="w-full px-4 py-2.5 bg-[var(--surface-muted)] hover:bg-[var(--border-light)] flex items-center justify-between text-left transition-colors"
                      >
                        <span className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wide">
                          {category}
                        </span>
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4 text-[var(--text-faint)]" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-[var(--text-faint)]" />
                        )}
                      </button>
                      {isExpanded && (
                        <div>
                          {categoryOptions.map((option) => {
                            const isSelected = isOptionSelected(option);
                            return (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => handleToggleSkill(option)}
                                className={`w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-[var(--primary-light)] transition-colors ${
                                  isSelected ? 'bg-[var(--primary-light)]' : ''
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <div
                                    className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                      isSelected
                                        ? 'bg-ah-brand-primary border-ah-brand-primary'
                                        : 'border-[var(--border)]'
                                    }`}
                                  >
                                    {isSelected && (
                                      <Check size={12} className="text-white" />
                                    )}
                                  </div>
                                  <span className="text-sm text-[var(--text)]">
                                    {option.label}
                                  </span>
                                </div>
                                {option.abbreviation && (
                                  <span className="text-xs text-[var(--text-faint)] bg-[var(--surface-muted)] px-2 py-0.5 rounded-full flex-shrink-0">
                                    {option.abbreviation}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }
              )}
            </div>
          )}

          {/* Show message when no options available */}
          {!searchTerm.trim() &&
            Object.keys(filteredGroupedOptions).length === 0 && (
              <div className="p-8 text-center text-[var(--text-faint)]">
                <p className="text-sm">No skills available</p>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}
